"use client";

import { ld } from "@/lib/theme/light-dark";
import { useTranslations } from "next-intl";
import type { ReactNode, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { H } from "../hero-tokens";
import { STAGE_TILE, STAGE_WAYS, StageCaption, StageResult, type StageWay } from "./scroll-stage-result";
import { FlyWindows } from "./scroll-stage-windows";
import "./scroll-stage.css";

const STAGE_WIDTH = 1080;
/** Authored fly schedule [AUTH_MIN..AUTH_MAX] remapped to [WIN_START..WIN_END]: clear by headline point. */
const AUTH_MIN = 0.3;
const AUTH_MAX = 0.858;
const WIN_START = 0.05;
const WIN_END = 0.48;
/** Timeline length of each paused fly animation; seeked to p × this per frame. */
const FLY_DURATION_MS = 1000;
const WAY_DURATION_MS = 4500;

/**
 * Scroll → Pw 0..1. "top" (desktop): canvas top from startOffset·vh over span·vh.
 * "center" (scaled phone stage): canvas center from start·vh to end·vh.
 */
type ScrollStageTiming =
  | { readonly mode: "top"; readonly startOffset: number; readonly span: number }
  | { readonly mode: "center"; readonly start: number; readonly end: number };

/** Way switch state: ERP first, turns every 4.5 s while the switch is on screen; a click restarts. */
export function useStageWay(): {
  readonly way: StageWay;
  readonly pick: (way: StageWay) => void;
  readonly switchRef: RefObject<HTMLDivElement | null>;
} {
  const [way, setWay] = useState<StageWay>("erp");
  const [turn, setTurn] = useState(0);
  const [inView, setInView] = useState(false);
  const switchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = switchRef.current;
    if (el === null) return;
    const observer = new IntersectionObserver(
      (entries) => setInView(entries.some((entry) => entry.isIntersecting)),
      { threshold: 0.6 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setTimeout(() => {
      setWay((current) => STAGE_WAYS[(STAGE_WAYS.indexOf(current) + 1) % STAGE_WAYS.length] ?? "erp");
    }, WAY_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [inView, way, turn]);

  const pick = useCallback((next: StageWay): void => {
    setWay(next);
    setTurn((current) => current + 1);
  }, []);

  return { way, pick, switchRef };
}

/** "Mit deinem ERP" · "Mit unserer Buchhaltung". */
export function StageWaySwitch(props: {
  readonly way: StageWay;
  readonly pick: (way: StageWay) => void;
  readonly switchRef: RefObject<HTMLDivElement | null>;
}): ReactNode {
  const t = useTranslations("marketing.scrollStage");
  const label: Record<StageWay, string> = { erp: t("wayErp"), books: t("wayBooks") };
  return (
    <div ref={props.switchRef} className="mkss-switch">
      {STAGE_WAYS.map((way) => (
        <button
          key={way}
          type="button"
          className="mkss-pill"
          data-active={way === props.way ? "true" : undefined}
          aria-pressed={way === props.way}
          onClick={() => props.pick(way)}
        >
          {label[way]}
        </button>
      ))}
    </div>
  );
}

/**
 * 1080px stage: nine windows of other programs fly into one point on scroll and
 * leave "Mach daraus eins" (or "zwei") with one or two app tiles. Desktop full
 * size, phone scaled. Reduced motion shows the end state.
 */
export function ScrollStageCanvas({
  timing,
  way,
  height,
  headlineTop,
  resultTop,
  scale = 1,
  switchNode,
  captionNode,
}: {
  readonly timing: ScrollStageTiming;
  readonly way: StageWay;
  readonly height: number;
  readonly headlineTop: number;
  readonly resultTop: number;
  /** Phone: undoes the ~0.34 stage scale for headline and tiles. */
  readonly scale?: number;
  /** Desktop renders switch and caption inside the canvas; the phone puts both below at native size. */
  readonly switchNode?: ReactNode;
  readonly captionNode?: ReactNode;
}): ReactNode {
  const t = useTranslations("marketing.scrollStage");
  const stageRef = useRef<HTMLDivElement | null>(null);
  const mode = timing.mode;
  const a = timing.mode === "top" ? timing.startOffset : timing.start;
  const b = timing.mode === "top" ? timing.span : timing.end;
  const targetY = resultTop + (STAGE_TILE * scale) / 2;

  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null) return;
    const flyEls = Array.from(stage.querySelectorAll<HTMLElement>("[data-fly]"));
    const fadeEls = Array.from(stage.querySelectorAll<HTMLElement>("[data-fade-in]"));

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      for (const el of flyEls) {
        el.style.transform = "scale(0.14)";
        el.style.opacity = "0";
      }
      for (const el of fadeEls) el.style.opacity = "1";
      return;
    }

    const c01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
    const ease = (x: number): number => (x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2);

    // Paused Web Animations seeked per frame: Safari scales on the GPU instead of repainting shadows.
    const fly = flyEls.map((el) => {
      const [rs = AUTH_MIN, re = AUTH_MAX] = (el.getAttribute("data-fly") ?? "").split(",").map(Number);
      // Every window lands on the tile center, wherever the layout puts it.
      const dx = STAGE_WIDTH / 2 - (el.offsetLeft + el.offsetWidth / 2);
      const dy = targetY - (el.offsetTop + el.offsetHeight / 2);
      const span = WIN_END - WIN_START;
      const rsAdj = WIN_START + ((rs - AUTH_MIN) / (AUTH_MAX - AUTH_MIN)) * span;
      const reAdj = WIN_START + ((re - AUTH_MIN) / (AUTH_MAX - AUTH_MIN)) * span;
      const anim = el.animate(
        [
          { transform: "translate(0px, 0px) scale(1)", opacity: 1, offset: 0 },
          { opacity: 1, offset: 0.18 },
          { opacity: 0, offset: 0.68 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.14)`, opacity: 0, offset: 1 },
        ],
        { duration: FLY_DURATION_MS, fill: "both" },
      );
      anim.pause();
      return { anim, rsAdj, reAdj, lastP: -1 };
    });
    const fades = fadeEls.map((el) => {
      const [from = 0, to = 1] = (el.getAttribute("data-fade-in") ?? "").split(",").map(Number);
      return { el, from, to, last: "" };
    });

    const update = (): void => {
      const r = stage.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      const Pw =
        mode === "center"
          ? c01((vh * a - (r.top + r.height / 2)) / (vh * (a - b)))
          : c01((vh * a - r.top) / (vh * b));
      for (const f of fly) {
        const p = ease(c01((Pw - f.rsAdj) / (f.reAdj - f.rsAdj)));
        if (p === f.lastP) continue;
        f.lastP = p;
        f.anim.currentTime = p * FLY_DURATION_MS;
      }
      for (const f of fades) {
        const opacity = c01((Pw - f.from) / (f.to - f.from)).toFixed(3);
        if (opacity === f.last) continue;
        f.last = opacity;
        f.el.style.opacity = opacity;
      }
    };

    // Loop only while near the viewport; leaving settles the clamped end state.
    let raf = 0;
    let running = false;
    const tick = (): void => {
      update();
      raf = running ? window.requestAnimationFrame(tick) : 0;
    };
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible && !running) {
          running = true;
          raf = window.requestAnimationFrame(tick);
        } else if (!visible && running) {
          running = false;
          window.cancelAnimationFrame(raf);
          update();
        }
      },
      { rootMargin: "50% 0px" },
    );
    observer.observe(stage);
    update();
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(raf);
      for (const f of fly) f.anim.cancel();
    };
  }, [mode, a, b, targetY]);

  return (
    <div ref={stageRef} style={{ position: "relative", width: STAGE_WIDTH, height }}>
      <FlyWindows />

      {/* Fades in while the windows still fly over it, fully legible once they are gone. */}
      <div
        data-fade-in="0.1,0.4"
        style={{
          position: "absolute",
          left: 0,
          top: headlineTop,
          width: STAGE_WIDTH,
          textAlign: "center",
          zIndex: 1,
          opacity: 0,
        }}
      >
        <h2
          style={{
            fontFamily: H.display,
            fontWeight: 400,
            fontSize: 50 * scale,
            lineHeight: 1.04,
            letterSpacing: "-0.02em",
            color: ld("#0B0F19", "#fafafa"),
            margin: 0,
          }}
        >
          {t.rich("machDaraus", {
            way,
            n: (chunks): ReactNode => (
              <span key={way} className="mkss-swap">
                {chunks}
              </span>
            ),
          })}
        </h2>
        {switchNode === undefined ? null : <div style={{ marginTop: 24 }}>{switchNode}</div>}
      </div>

      {/* The windows shrink into this point, the tiles appear as they arrive. */}
      <div
        data-fade-in="0.2,0.42"
        style={{ position: "absolute", left: 0, top: resultTop, width: STAGE_WIDTH, zIndex: 50, opacity: 0 }}
      >
        <StageResult way={way} scale={scale} />
        {captionNode === undefined ? null : <div style={{ marginTop: 26 }}>{captionNode}</div>}
      </div>
    </div>
  );
}

/** Desktop: "Acht Uhr und schon zwölf Programme offen" above the full-size stage. */
export function ScrollStage(): ReactNode {
  const t = useTranslations("marketing.scrollStage");
  const { way, pick, switchRef } = useStageWay();
  return (
    <section
      aria-labelledby="scroll-stage-heading"
      style={{ position: "relative", margin: "130px auto 0", maxWidth: 1240, padding: "0 20px" }}
    >
      <div style={{ textAlign: "center", maxWidth: 900, margin: "0 auto" }}>
        <h2
          id="scroll-stage-heading"
          style={{
            fontFamily: H.display,
            fontWeight: 400,
            fontSize: "clamp(2rem, 4vw, 3.25rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.018em",
            textWrap: "balance",
            color: ld("#0B0F19", "#fafafa"),
            margin: 0,
          }}
        >
          {t("achtUhrProgrammeOffen")}
        </h2>
        <p
          style={{
            fontSize: "clamp(1rem, 1.3vw, 1.125rem)",
            lineHeight: 1.55,
            color: ld("rgba(11,15,25,0.6)", "rgba(250,250,250,0.6)"),
            margin: "16px auto 0",
            maxWidth: 560,
          }}
        >
          {t("jederVorgangVerteiltMehrere")}
        </p>
      </div>
      <div style={{ width: STAGE_WIDTH, margin: "54px auto 0" }}>
        <ScrollStageCanvas
          timing={{ mode: "top", startOffset: 0.5, span: 1.6 }}
          way={way}
          height={940}
          headlineTop={480}
          resultTop={660}
          switchNode={<StageWaySwitch way={way} pick={pick} switchRef={switchRef} />}
          captionNode={<StageCaption way={way} />}
        />
      </div>
    </section>
  );
}
